import { useEffect, useState } from "react";
import { CalendarCheck, Check, Clock, ExternalLink, MapPin, Share2, Sparkles } from "lucide-react";
import { cn } from "@/lib/cn";
import { localizeRestaurant } from "@/lib/localizeRestaurant";
import { formatOpeningHours, isRestaurantOpen, openStatusDetail } from "@/lib/openingHours";
import { safeHref } from "@/lib/safeUrl";
import { track } from "@/lib/analytics";
import {
  effectiveReservationsStatus,
  type EffectiveReservationsStatus,
} from "@/lib/reservations";
import { useTaxonomy } from "@/data/TaxonomyProvider";
import { useTranslation } from "@/i18n/I18nProvider";
import { isOneTimeEnded } from "@/hooks/useActiveDiscount";
import { useShowcase, useViewerSignedIn } from "@/services/showcase";
import { venuePath, venueSlugOf } from "@/lib/venueUrl";
import type { GeolocationState } from "@/hooks/useGeolocation";
import type { Discount, Restaurant } from "@/types/restaurant";
import type { TranslationKey } from "@/i18n/translations";
import { OfferCard } from "./OfferCard";
import { BonusVenueChip } from "./BonusVenueChip";
import { DineInButton } from "./DineInButton";
import { ReportLink } from "./ReportLink";
import { DetailGallery } from "./DetailGallery";

// Reservation load signal (§27) — status line copy + quiet tone per level.
const RES_LINE_KEY: Record<EffectiveReservationsStatus, TranslationKey> = {
  neutral: "RES_DETAIL_NEUTRAL",
  available: "RES_STATUS_AVAILABLE",
  limited: "RES_STATUS_LIMITED",
  full: "RES_STATUS_FULL",
};

const RES_LINE_TEXT: Record<EffectiveReservationsStatus, string> = {
  neutral: "text-zinc-100 light:text-zinc-900",
  available: "text-emerald-400 light:text-emerald-600",
  limited: "text-amber-300 light:text-amber-600",
  full: "text-rose-300 light:text-rose-500",
};

export interface RestaurantDetailProps {
  restaurant: Restaurant;
  /** This venue's active offers, shown under the map (live state only affects the pill). */
  discounts?: Discount[];
  /** Wired to the dialog's aria-labelledby. */
  titleId?: string;
  /** Foreground location — enables the proximity-gated "Dine In" check-in. */
  geo?: GeolocationState;
  /** Called after a check-in when the diner wants to leave feedback. */
  onLeaveFeedback?: (visitId: number) => void;
  /** Opens the sidebar's stamp card (from the check-in's stamp line). */
  onOpenBonus?: () => void;
}

/**
 * The venue page (venue-detail-offers.md): shown as the detail modal over the
 * feed, with the venue's own URL. One column at every width: the gallery on
 * top (identity + open/closed with the hours on the photo), then Dine In,
 * bonus, reservations, tags, description, the Maps card, offers, Share/Report.
 */
export function RestaurantDetail({
  restaurant,
  discounts,
  titleId,
  geo,
  onLeaveFeedback,
  onOpenBonus,
}: RestaurantDetailProps) {
  const { t, lang } = useTranslation();
  const { description } = localizeRestaurant(restaurant, lang);
  const { cuisineLabels, tagLabels } = useTaxonomy();
  const cuisine = cuisineLabels(restaurant.cuisine_slugs, lang).join(" · ");
  const detailTags = tagLabels(restaurant.tag_slugs, lang);
  const images = [restaurant.image_url, ...(restaurant.gallery_image_urls ?? [])].filter(Boolean);

  // Tick once a minute so the live/scheduled pill on offers stays accurate.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  const offers = (discounts ?? []).filter((d) => !isOneTimeEnded(d, now));
  // Operator-authored URL — only rendered as a link when the scheme is safe.
  const mapsHref = safeHref(restaurant.maps_url);
  // Reservation load signal (§27): guests see the neutral marker, signed-in
  // users the staff-set level. Purely informational — Resto takes no bookings.
  const signedIn = useViewerSignedIn();
  const resStatus = effectiveReservationsStatus(restaurant, signedIn, now);
  // Every venue opens its page, closed ones too (Rule 1), so the photo says
  // whether it's open and its hours: a link from Google never lands on nothing.
  const isOpen = isRestaurantOpen(restaurant, now);
  const hours = openStatusDetail(restaurant, isOpen);
  const status = isOpen
    ? hours.kind === "until"
      ? t("CARD_OPEN_UNTIL", { time: hours.time })
      : t("CARD_OPEN")
    : hours.kind === "opens"
      ? t("CARD_CLOSED_OPENS", { time: hours.time })
      : t("CARD_CLOSED");

  // Share the venue's own page (no permission prompt: the native sheet, or a copy).
  const showcase = useShowcase();
  const path = showcase ? null : venuePath(restaurant.city, venueSlugOf(restaurant));
  const [copied, setCopied] = useState(false);
  async function share() {
    if (!path) return;
    const url = `${window.location.origin}${path}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: restaurant.name, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // The visitor closed the share sheet, or the clipboard is blocked: nothing to do.
    }
  }

  return (
    <div>
      <DetailGallery
        images={images}
        alt={restaurant.name}
        className="aspect-[4/3] rounded-t-2xl sm:aspect-[16/8] sm:rounded-t-3xl lg:aspect-[16/7]"
      >
        {/* Poster overlay: identity sits on the image like a marquee card. The
            photo keeps its dark scrim in both themes, so the text stays light. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 p-4 sm:p-6 lg:p-8">
          <p className="font-mono text-3xs uppercase tracking-[0.22em] text-zinc-300/90 sm:text-2xs">
            {restaurant.neighborhood ?? "–"} · {cuisine || "–"}
          </p>
          <h2
            id={titleId}
            className="mt-1.5 font-display text-[1.65rem] leading-[1.02] text-zinc-50 drop-shadow-[0_2px_14px_rgba(0,0,0,0.65)] sm:text-4xl lg:text-[2.6rem]"
          >
            {restaurant.name}
          </h2>
          <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs font-medium text-zinc-200 drop-shadow-[0_1px_8px_rgba(0,0,0,0.6)] sm:text-sm">
            <span className="inline-flex items-center gap-1.5">
              {isOpen ? (
                <span className="h-1.5 w-1.5 rounded-full bg-pulse" aria-hidden />
              ) : (
                <Clock className="h-3.5 w-3.5" aria-hidden />
              )}
              {status}
            </span>
            <span className="text-zinc-400" aria-hidden>·</span>
            <span className="text-zinc-300">
              {t("DETAIL_HOURS_DAILY", { hours: formatOpeningHours(restaurant) })}
            </span>
          </p>
        </div>
      </DetailGallery>

      {/* Info */}
      <div className="space-y-4 p-4 sm:p-6 lg:space-y-5 lg:p-8">
        {geo && onLeaveFeedback && (
          <DineInButton
            restaurant={restaurant}
            geo={geo}
            onLeaveFeedback={onLeaveFeedback}
            onOpenBonus={onOpenBonus}
          />
        )}

        {/* Bonus item + condition, guests included (Rule 7). Not in the demo. */}
        {geo && <BonusVenueChip venueId={restaurant.id} />}

        {resStatus !== null && (
          <div className="flex items-center gap-3 rounded-xl border border-white/[0.12] light:border-black/10 bg-zinc-900/60 light:bg-white/70 p-3 shadow-[0_1px_0_rgba(255,255,255,0.05)_inset]">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-white/[0.12] light:border-black/10 bg-zinc-800 light:bg-zinc-200 text-zinc-300 light:text-zinc-700">
              <CalendarCheck className="h-5 w-5" aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-mono text-3xs uppercase tracking-[0.18em] text-dim light:text-zinc-600">
                {t("RES_DETAIL_TITLE")}
              </span>
              <span className={cn("block text-sm font-medium", RES_LINE_TEXT[resStatus])}>
                {t(RES_LINE_KEY[resStatus])}
              </span>
              {resStatus !== "neutral" && (
                <span className="block text-xs text-dim light:text-zinc-600">{t("RES_DETAIL_HINT")}</span>
              )}
            </span>
          </div>
        )}

        {detailTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {detailTags.map((label) => (
              <span
                key={label}
                className="rounded-md border border-white/[0.12] light:border-black/10 bg-zinc-800/60 light:bg-zinc-200/60 px-2 py-0.5 text-xs text-zinc-200 light:text-zinc-800"
              >
                {label}
              </span>
            ))}
          </div>
        )}

        {description && (
          <p className="whitespace-pre-line text-sm leading-relaxed text-zinc-300 light:text-zinc-700">
            {description}
          </p>
        )}

        {mapsHref && (
          <a
            href={mapsHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              track("directions_tap", {
                venue_id: restaurant.id,
                city: restaurant.city ?? undefined,
                surface: "detail",
              })
            }
            className="group flex items-center gap-3 rounded-xl border border-white/[0.12] light:border-black/10 bg-zinc-900/60 light:bg-white/70 p-3 shadow-[0_1px_0_rgba(255,255,255,0.05)_inset] transition hover:border-white/30 light:hover:border-black/30"
          >
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-white/[0.12] light:border-black/10 bg-zinc-800 light:bg-zinc-200 text-zinc-300 light:text-zinc-700 transition group-hover:text-pulse">
              <MapPin className="h-5 w-5" aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-zinc-100 light:text-zinc-900">{t("DETAIL_MAP_TITLE")}</span>
              <span className="block truncate text-xs text-dim light:text-zinc-600">
                {restaurant.neighborhood ?? t("DETAIL_MAP_SUBTITLE")}
              </span>
            </span>
            <ExternalLink
              className="h-4 w-4 shrink-0 text-dim light:text-zinc-600 transition group-hover:text-zinc-300 light:group-hover:text-zinc-700"
              aria-hidden
            />
          </a>
        )}

        {offers.length > 0 && (
          <section className="space-y-2.5 border-t border-white/10 light:border-black/10 pt-4">
            <h3 className="flex items-center gap-1.5 font-mono text-3xs font-medium uppercase tracking-[0.2em] text-zinc-400 light:text-zinc-600">
              <Sparkles className="h-3.5 w-3.5 text-amber-300" aria-hidden />
              {t("OFFERS_SECTION")}
            </h3>
            <div className="flex flex-col gap-2">
              {offers.map((d) => (
                <OfferCard key={d.id} discount={d} now={now} venueName={restaurant.name} />
              ))}
            </div>
          </section>
        )}

        <div className="flex items-center justify-between gap-3 border-t border-white/10 light:border-black/10 pt-4">
          {path ? (
            <button
              type="button"
              onClick={() => void share()}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.14] light:border-black/10 px-3 py-1.5 text-xs font-medium text-zinc-200 light:text-zinc-800 transition hover:border-white/40 light:hover:border-black/30"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-pulse" aria-hidden /> : <Share2 className="h-3.5 w-3.5" aria-hidden />}
              <span aria-live="polite">{copied ? t("DETAIL_LINK_COPIED") : t("DETAIL_SHARE")}</span>
            </button>
          ) : (
            <span />
          )}
          <ReportLink
            label={restaurant.name}
            target={{ kind: "venue", restaurantId: restaurant.id, restaurantName: restaurant.name }}
          />
        </div>
      </div>
    </div>
  );
}
